
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { useState } from "react";

interface OriginStoryProps {
  onOpenChange?: (open: boolean) => void;
  isOpen?: boolean;
}

export function OriginStory({
  onOpenChange,
  isOpen
}: OriginStoryProps) {
  const [open, setOpen] = useState(isOpen || false);
  
  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (onOpenChange) onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button 
          variant="outline" 
          className="w-full text-black border-gray-300 hover:bg-gray-100 hover:text-black text-lg font-medium rounded-lg px-5 py-4 h-auto"
        >
          Origin Story
        </Button>
      </DialogTrigger>
      
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">The Origin Story</DialogTitle>
        </DialogHeader>
        <ScrollArea className="h-[70vh] pr-4">
          <div className="space-y-6">
            <div>
              <p className="text-base leading-relaxed">
                Match My Logic was born from a simple observation: even the closest friends and family can see the world completely differently.
              </p>
            </div>

            <div>
              <p className="text-base leading-relaxed">For us - Cynthia and Jason Benjamin - this insight sparked something special during one of our late-night conversations while at home in Los Feliz, CA. Even after almost 10 years of together, we found ourselves exploring how differently we each saw the world. The magic first sparked when we challenged ourselves to organize simple words in ways that made sense to us - but what seemed obvious to one person was completely unexpected to the other. A word like 'sentence' could be seen as something tall or short, long-lasting or fleeting, depending on who was looking at it. These different viewpoints sparked laughter, debate, and deeper understanding.</p>
            </div>

            <div>
              <p className="text-base leading-relaxed">
                As we shared the earliest prototype with friends during our game nights, it took on a life of its own. Someone would confidently call out 'Locked in!' when they were sure they'd cracked another player's logic, building anticipation around the table. The most exciting moments weren't just about getting the order right - they were about those rare instances of 'Perfect Logic' when everyone saw things exactly the same way, creating an electric connection around the table.
              </p>
            </div>

            <div>
              <p className="text-base leading-relaxed">
                We designed Match My Logic with a timeless feel because understanding each other never goes out of style. It bridges the energy of social party games with the thoughtfulness of strategy games, making space for both the outgoing storyteller and the quiet observer. While we've never created a game before, we're lifelong game players who believe in the power of play to bring people together.
              </p>
            </div>

            <div>
              <p className="text-base leading-relaxed">
                At its heart, Match My Logic isn't just about guessing right - it's about those moments when we truly understand how someone else thinks, and in turn, learn something new about ourselves.
              </p>
            </div>

            <div>
              <p className="text-base leading-relaxed">As Los Angeles creatives who love bringing people together, we're excited to share Match My Logic: Daily - a simple daily puzzle that lets you race against time and see if you and your friends match each other, the game or both! While you enjoy these daily brain teasers, know we're hard at work preparing the full physical card game that will bring all the creative, mind-bendy elements of Match My Logic to life. Our Kickstarter campaign is just around the corner and you'll be one of the first to know when it's live. We can't wait to get the complete Match My Logic experience into your hands and onto your game tables.</p>
            </div>
            <div className="flex flex-col items-center gap-4">
              <img src="/lovable-uploads/05391b2f-0c02-44fe-b017-f880c0a58895.png" alt="The creators of Match My Logic" className="w-40 h-40 object-cover rounded-full" />
              <p className="text-base leading-relaxed font-semibold">
                Jason & Cynthia Benjamin<br />
                Creators, Match My Logic
              </p>
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
